import { Loader } from '@googlemaps/js-api-loader';

type GoogleMapsLibraryId = 'maps' | 'places' | 'geocoding' | 'marker' | 'core';

const LIBRARY_WAIT_TIMEOUT_MS = 10000;
const LIBRARY_WAIT_INTERVAL_MS = 50;

let sharedLoader: Loader | null = null;
let sharedLoaderApiKey: string | undefined;
const libraryLoadPromises = new Map<string, Promise<void>>();

/** True when the Maps JS API core library (Map constructor) is available. */
export const isGoogleMapsMapReady = (): boolean =>
  typeof window !== 'undefined' && typeof window.google?.maps?.Map === 'function';

/** True when Places AutocompleteService is available. */
export const isGoogleMapsPlacesReady = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.google?.maps?.places?.AutocompleteService === 'function';

const isLibraryReady = (library: GoogleMapsLibraryId): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  switch (library) {
    case 'maps':
    case 'core':
      return isGoogleMapsMapReady();
    case 'places':
      return isGoogleMapsPlacesReady();
    case 'geocoding':
      return typeof window.google?.maps?.Geocoder === 'function';
    case 'marker':
      return typeof window.google?.maps?.Marker === 'function';
    default:
      return false;
  }
};

/** True when any Google Maps namespace is already on the page (any loader). */
export const hasGoogleMapsNamespace = (): boolean =>
  typeof window !== 'undefined' && Boolean(window.google?.maps);

/** True when a Maps JS bootstrap script tag is already in the document. */
export const hasExistingGoogleMapsScript = (): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }

  return Array.from(document.getElementsByTagName('script')).some(
    (script) => typeof script.src === 'string' && script.src.includes('maps.googleapis.com')
  );
};

const waitForLibraryReady = (
  library: GoogleMapsLibraryId,
  timeoutMs = LIBRARY_WAIT_TIMEOUT_MS
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (isLibraryReady(library)) {
      resolve();
      return;
    }

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (isLibraryReady(library)) {
        window.clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(intervalId);
        reject(new Error(`Timed out waiting for Google Maps library '${library}'`));
      }
    }, LIBRARY_WAIT_INTERVAL_MS);
  });

/**
 * When Maps was loaded by another path (e.g. legacy useLoadScript), reuse the
 * existing bootstrap via google.maps.importLibrary or wait — never inject again.
 */
const loadLibraryWithoutReinjection = async (library: GoogleMapsLibraryId): Promise<void> => {
  if (isLibraryReady(library)) {
    return;
  }

  const mapsImportLibrary = (
    window.google?.maps as { importLibrary?: (id: string) => Promise<unknown> } | undefined
  )?.importLibrary;

  if (typeof mapsImportLibrary === 'function') {
    await mapsImportLibrary(library);
    return;
  }

  await waitForLibraryReady(library);
};

const getSharedLoader = (apiKey: string): Loader => {
  if (sharedLoader && sharedLoaderApiKey === apiKey) {
    return sharedLoader;
  }

  sharedLoader = new Loader({
    apiKey,
    version: 'weekly',
  });
  sharedLoaderApiKey = apiKey;
  libraryLoadPromises.clear();
  return sharedLoader;
};

/**
 * Loads a Google Maps JS library via a process-wide singleton Loader.
 * Concurrent callers share the same in-flight promise per library id.
 * Never re-injects when Maps is already present from another loader.
 */
export const importGoogleMapsLibrary = async (
  library: GoogleMapsLibraryId,
  mapApiKey?: string
): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  if (isLibraryReady(library)) {
    return;
  }

  const existingPromise = libraryLoadPromises.get(library);
  if (existingPromise) {
    await existingPromise;
    return;
  }

  if (hasGoogleMapsNamespace() || hasExistingGoogleMapsScript()) {
    const waitPromise = loadLibraryWithoutReinjection(library);
    libraryLoadPromises.set(library, waitPromise);
    try {
      await waitPromise;
    } catch (error) {
      libraryLoadPromises.delete(library);
      throw error;
    }
    return;
  }

  if (!mapApiKey) {
    return;
  }

  const loadPromise = getSharedLoader(mapApiKey)
    .importLibrary(library)
    .then(() => undefined);

  libraryLoadPromises.set(library, loadPromise);

  try {
    await loadPromise;
  } catch (error) {
    libraryLoadPromises.delete(library);
    throw error;
  }
};

/**
 * Loads the Google Maps core library when only Places/Geocoding was loaded elsewhere
 * (e.g. Location Search autocomplete). Safe to call multiple times.
 */
export const loadGoogleMapsMapLibrary = async (mapApiKey?: string): Promise<void> => {
  await importGoogleMapsLibrary('maps', mapApiKey);
};

/** Loads the Places library. Safe to call multiple times. */
export const loadGoogleMapsPlacesLibrary = async (mapApiKey?: string): Promise<void> => {
  await importGoogleMapsLibrary('places', mapApiKey);
};

/** Test-only: clears the singleton Loader and in-flight library promises. */
export const __resetGoogleMapsLoaderForTests = (): void => {
  sharedLoader = null;
  sharedLoaderApiKey = undefined;
  libraryLoadPromises.clear();
};
